import { useCallback, useEffect, useRef, useState } from 'react';

export const FIRST_COLUMN_SHADOW =
  '4px 0 8px -2px rgba(0, 0, 0, 0.12), 2px 0 4px -1px rgba(0, 0, 0, 0.06)';
export const LAST_COLUMN_SHADOW =
  '-4px 0 8px -2px rgba(0, 0, 0, 0.12), -2px 0 4px -1px rgba(0, 0, 0, 0.06)';

function getScrollContainer(el: HTMLElement | null): HTMLElement | null {
  if (!el) return null;
  let current = el.parentElement;
  while (current) {
    const style = window.getComputedStyle(current);
    if (style.overflowX === 'auto' || style.overflowX === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }
  return el.parentElement;
}

export function useTableScroll() {
  const tableRef = useRef<HTMLTableElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const table = tableRef.current;
    if (!table) return;
    const container = getScrollContainer(table);
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const isScrollable = scrollWidth > clientWidth;

    setCanScrollLeft(isScrollable && scrollLeft > 2);
    setCanScrollRight(isScrollable && Math.ceil(scrollLeft) < scrollWidth - clientWidth - 2);
  }, []);

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    const container = getScrollContainer(table);
    if (!container) return;

    checkScroll();
    const frameId = requestAnimationFrame(checkScroll);

    container.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);

    const resizeObserver = new ResizeObserver(() => {
      checkScroll();
    });
    resizeObserver.observe(container);
    resizeObserver.observe(table);

    return () => {
      cancelAnimationFrame(frameId);
      container.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
      resizeObserver.disconnect();
    };
  }, [checkScroll]);

  return { tableRef, canScrollLeft, canScrollRight, checkScroll };
}
